import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import InvoiceAnalyzerWrapper from './Wrapper';
import Uploader, { UploaderProps } from './Uploader';
import { useModal } from 'hooks/useModal';
import LoadingScreen from './LoadingScreen';
import { Modal } from 'components/Modal/Modal';
import ComparisonQuote from './ComparisonQuote';
import Rebill from './Rebill';
import LinkAccountForm from './LinkAccountForm';
import { useQuery } from 'react-query';
import {
  getInvoiceComparison,
  getInvoiceDetails,
  processInvoice,
} from 'api/orders/api';
import { InvoiceComparisonQuotes } from 'api/orders/schemas';
import RebillServiceDetailsModal from 'screens/Inventory/NonGRTInvoices/RebillServiceDetailsModal';
import SuccessModal from 'screens/Inventory/NonGRTInvoices/RebillServiceDetailsModal/SuccessModal';
import LOAModal from 'screens/Inventory/NonGRTInvoices/LOAModal';
import LOASubmittedModal from 'screens/Inventory/NonGRTInvoices/LOAModal/LOASubmittedModal';
import { useNavigate } from 'react-router-dom';
import {
  addOrderItems,
  getLastDraftOrder,
  postOrder,
} from 'api/accessexpressv3/api';
import ReviewRebillModal from 'screens/Inventory/NonGRTInvoices/ReviewRebillModal';
import { InvoiceProcessingStatus, isGraniteHQAddress } from './utils';
import { notificationDispatcher } from 'components/Notifications/utils';

const InvoiceAnalyzer: FC<Pick<UploaderProps, 'type'>> = ({ type }) => {
  const navigate = useNavigate();
  const loadingScreen = useModal();
  const comparisonQuote = useModal();
  const rebill = useModal();
  const linkAccount = useModal();

  const [uploadId, setUploadId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [loadingScreenStatus, setLoadingScreenStatus] =
    useState<InvoiceProcessingStatus>('default');

  const [location, setLocation] = useState<{
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
    id?: string;
  } | null>(null);

  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [isComparing, setIsComparing] = useState(false);

  const [comparisonData, setComparisonData] =
    useState<InvoiceComparisonQuotes>();

  const [stopPolling, setStopPolling] = useState(false);

  const uploadInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (comparisonData) {
      loadingScreen.close();
      comparisonQuote.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonData]);

  const startProgressSimulation = (n?: number) => {
    setProgress(n ?? 10);
    uploadInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 90) {
          const next = prev + 10;
          return next > 90 ? 90 : next;
        }
        return prev;
      });
    }, 10000);
  };

  const stopProgressSimulation = (finalValue: number) => {
    if (uploadInterval.current) clearInterval(uploadInterval.current);
    setProgress(finalValue);
  };

  const handleInvoiceUploaded = async (id: string) => {
    const pollProcess = async () => {
      try {
        const res = await processInvoice(id);

        if (!res) return;

        if (isGraniteHQAddress(res.location)) {
          stopProgressSimulation(100);
          loadingScreen.close();
          rebill.open();
          return;
        }

        if (res.status === 'Checking availability') {
          setTimeout(pollProcess, 3000);
          return;
        }

        setQuoteId(res.id);

        if (res.status === 'Order services') {
          if (res.location) {
            setLocation(res.location);
          }

          stopProgressSimulation(100);
          setLoadingScreenStatus('address-verification');
        } else {
          setLoadingScreenStatus('no-better-offer');
        }
      } catch (err) {
        console.error('Failed to process invoice', err);
        stopProgressSimulation(0);
        loadingScreen.close();
      }
    };

    pollProcess();
  };

  const onAddressConfirmation = async () => {
    if (!quoteId) return;

    try {
      setIsComparing(true);
      const compareData = await getInvoiceComparison(quoteId);
      if (compareData) {
        const [graniteQuoteLocation] =
          compareData?.granite_quote?.locations ?? [];

        const [bestOffer] = graniteQuoteLocation?.best_offerings ?? [];

        if (!bestOffer) {
          setLoadingScreenStatus('no-better-offer');
          setIsComparing(false);
          return;
        }
        setComparisonData(compareData);
      }
    } catch (err) {
      console.error('Failed to fetch invoice comparison', err);
    } finally {
      setIsComparing(false);
    }
  };

  useQuery(
    ['invoice-analyzer-quote-loading', uploadId],
    () => getInvoiceDetails(uploadId as string),
    {
      enabled: !!uploadId && loadingScreen.isOpen && !stopPolling,
      refetchInterval: (data) =>
        data?.processing_status === 'pending action' ? false : 5000,
      onSuccess: (data) => {
        if (data.processing_status === 'pending action' && uploadId) {
          setStopPolling(true);
          handleInvoiceUploaded(uploadId);
        }

        if (
          [
            'failed-granite-invoice',
            'failed-multi-site',
            'failed-multi-service',
          ].includes(data.processing_status)
        ) {
          setStopPolling(true);

          stopProgressSimulation(0);
          setLoadingScreenStatus(data.processing_status);
          return;
        }

        if (data.processing_status === 'failed' && uploadId) {
          setStopPolling(true);
          stopProgressSimulation(0);
          loadingScreen.close();
          rebill.open();
        }
      },
      onError: () => {
        stopProgressSimulation(0);
        loadingScreen.close();
      },
    },
  );

  const { open: openRebillServiceDetailsModal, ...rebillModalProps } =
    useModal();
  const { open: openSuccessModal, ...successModalProps } = useModal();
  const { open: openReviewModal, ...reviewModalProps } = useModal();

  const { open: openLOAModal, ...loaModalProps } = useModal();
  const { open: openSubmittedLOAModal, ...submittedLOAModalProps } = useModal();

  const handleCheckout = async () => {
    if (!comparisonData) return;

    const firstLocation = comparisonData.granite_quote.locations?.[0];
    const firstProduct = firstLocation?.best_offerings?.[0];
    if (!firstLocation || !firstProduct) return;

    const productType =
      firstProduct.formal_broadband_product ?? firstProduct.formal_dia_product;

    const orderItems = [
      {
        quantity: 1,
        price:
          (productType?.formal_broadband_pricing?.total_mrc ??
            productType?.formal_broadband_pricing?.total_mrc) ||
          0,
        item_details: {
          ...productType,
          ...firstProduct,
          type: firstProduct.formal_broadband_product
            ? 'broadband'
            : firstProduct.formal_dia_product
              ? 'dia'
              : '',
          product_type: firstProduct.formal_broadband_product
            ? 'Broadband'
            : firstProduct.formal_dia_product
              ? 'DIA'
              : '',
          offering_id: productType?.id,
          invoice_id: invoiceId,
        },
        type: 'access_service',
        subtype: firstProduct.formal_broadband_product
          ? 'broadband'
          : firstProduct.formal_dia_product
            ? 'dia'
            : '',
        pim_price_id: null,
        location_id: '',
        category: 'Access',
      },
    ];

    try {
      const existingDraft = await getLastDraftOrder();

      if (existingDraft?.id && existingDraft.locations?.[0]) {
        await addOrderItems(
          { order_items: orderItems },
          existingDraft.locations[0].id,
        );
      } else {
        const orderPayload = {
          locations: [
            {
              address: {
                ...location,
                address_line_1: location?.address1 || firstLocation.address1,
                address_line_2: location?.address2 || undefined,
                zip: location?.zip_code || firstLocation.zip_code,
                city: (location?.city || firstLocation.city) as string,
                state: (location?.state || firstLocation.state) as string,
              },
              order_items: orderItems,
            },
          ],
          order_items: orderItems,
          customer_name: comparisonData.granite_quote.customer_name,
          requester: comparisonData.granite_quote.customer_name,
          status: 'draft',
        };

        await postOrder(orderPayload);
      }

      navigate(`/access-express/v3/checkout/access`);
    } catch (err) {
      console.error('Failed to prepare order from InvoiceAnalyzer', err);
    }
  };

  const onReset = () => {
    setUploadId(null);
    setProgress(0);
    setLoadingScreenStatus('default');
    setLocation(null);
    setQuoteId(null);
    setIsComparing(false);
    setComparisonData(undefined);
    setStopPolling(false);

    if (uploadInterval.current) {
      clearInterval(uploadInterval.current);
      uploadInterval.current = null;
    }
    rebillModalProps.close();
    loadingScreen.close();
    comparisonQuote.close();
    rebill.close();
    linkAccount.close();
  };

  const handleInvoiceSelected = useCallback(
    (event: { event: { id: string } }) => {
      onReset();
      const id = event.event.id;

      setUploadId(id);
      setInvoiceId(id);
      setProgress(100);
      loadingScreen.open();
      startProgressSimulation(100);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingScreen],
  );

  useEffect(() => {
    const unsubscribe = notificationDispatcher.on(
      'set-invoice-id',
      handleInvoiceSelected,
    );
    return () => unsubscribe();
  }, [handleInvoiceSelected]);

  return (
    <InvoiceAnalyzerWrapper type={type}>
      <Uploader
        type={type}
        onUploadSuccess={(id, invoiceId) => {
          setUploadId(id);
          setInvoiceId(invoiceId);
          setProgress(0);
          loadingScreen.open();
          startProgressSimulation();
        }}
        onReset={onReset}
      />

      <Modal
        isVisible={loadingScreen.isOpen}
        close={loadingScreen.close}
        className="w-full max-w-[800px] !rounded !bg-[linear-gradient(0deg,_#20202D,_#20202D)]"
      >
        <LoadingScreen
          status={loadingScreenStatus}
          progress={progress}
          onClose={() => {
            comparisonQuote.close();
            loadingScreen.close();
          }}
          location={location || undefined}
          onConfirmAddress={onAddressConfirmation}
          onRejectAddress={() => loadingScreen.close()}
          isComparing={isComparing}
          onConsolidate={() => {
            loadingScreen.close();
            rebill.open();
          }}
        />
      </Modal>

      <Modal
        isVisible={comparisonQuote.isOpen}
        close={comparisonQuote.close}
        enableScrolling={true}
        className="max-h-[90vh] w-full max-w-[1200px] !rounded !bg-background-base-surface-2"
      >
        <ComparisonQuote
          //@ts-expect-error err
          data={comparisonData}
          onOtherOptionsClick={rebill.open}
          onCheckout={handleCheckout}
        />
      </Modal>

      <Modal
        isVisible={rebill.isOpen}
        close={rebill.close}
        className="vertical-scrollbar max-h-[90vh] w-full max-w-[1200px] !rounded !bg-background-base-surface-2"
      >
        <Rebill
          onBackClick={
            location
              ? () => {
                  rebill.close();
                  comparisonQuote.open();
                }
              : undefined
          }
          onConsolidateServiceClick={() => {
            rebill.close();
            openRebillServiceDetailsModal();
          }}
          location={location || undefined}
        />
      </Modal>

      <Modal
        isVisible={linkAccount.isOpen}
        close={linkAccount.close}
        className="w-full max-w-[1012px]"
      >
        <LinkAccountForm />
      </Modal>

      <RebillServiceDetailsModal
        {...rebillModalProps}
        onConfirm={() => {
          // openSuccessModal();

          rebill.close();
          comparisonQuote.close();
          loadingScreen.close();
          setUploadId(null);
        }}
        invoiceId={invoiceId || undefined}
      />
      <SuccessModal
        {...successModalProps}
        onConfirm={() => openReviewModal()}
      />
      <ReviewRebillModal
        {...reviewModalProps}
        onConfirm={() => openLOAModal()}
      />
      <LOAModal {...loaModalProps} onConfirm={() => openSubmittedLOAModal()} />
      <LOASubmittedModal {...submittedLOAModalProps} />
    </InvoiceAnalyzerWrapper>
  );
};

export default InvoiceAnalyzer;
